/**
 * The comment threads on the open merge request - issue #99, phase B.
 *
 * BEST READS AND FORWARDS; GITLAB ENFORCES. The threads are GitLab's, listed,
 * started, replied to and resolved as the signed-in person; BEST holds no
 * review policy of its own. Who sees the controls is the screen's writer check
 * (`canComment`), the same one that gates its other buttons, and GitLab still
 * has the last word on each write: a refusal arrives as a 403 and is shown in
 * GitLab's own words as a toast, never as "sign in again".
 *
 * WHAT A NOTE IS. A note's text was typed by a person, so it is rendered as
 * ESCAPED PLAIN TEXT - a React text node, line breaks kept by CSS - and never as
 * HTML. The front end has no markdown renderer that is safe by construction
 * (nothing in this project sanitises), so none is used: `**bold**` shows as
 * typed, and the link beside each note goes to GitLab for the formatted one.
 *
 * This is what makes GitLab's "discussions must be resolved" merge blocker
 * workable from the page: the count below the heading is that rule's number.
 */

import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { api } from '../../api'
import type { ApiError } from '../../api'
import type { DiscussionList, DiscussionNote, DiscussionThread } from '../../schema'
import { useToasts } from '../../state/Toasts'
import { MAX_COMMENT, positionLabel, unresolvedText } from './discussions'
import EditElementButton from './EditElementButton'
import type { EditTarget } from './elementThreads'

/** A time as the person's locale writes it; GitLab's own text if it will not parse. */
function when(stamp: string): string {
  const parsed = new Date(stamp)
  return Number.isNaN(parsed.getTime()) ? stamp : parsed.toLocaleString()
}

/** A link is drawn only for an http(s) address, whatever the server sent. */
function safeLink(url: string): string | null {
  return /^https?:\/\//i.test(url) ? url : null
}

function Note({ note }: { note: DiscussionNote }) {
  const link = safeLink(note.web_url)
  return (
    <Box data-testid="discussion-note">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {note.author.name || note.author.username || 'Someone'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {when(note.created_at)}
        </Typography>
        {link ? (
          <Link
            href={link}
            target="_blank"
            rel="noreferrer"
            variant="caption"
            aria-label={`Open ${note.author.username || 'this'} note in GitLab`}
          >
            In GitLab
          </Link>
        ) : null}
      </Stack>
      {/* A text node: whatever the body contains is shown, not interpreted. */}
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {note.body}
      </Typography>
      {note.truncated ? (
        <Typography variant="caption" color="text.secondary">
          Shortened here - the rest is in GitLab.
        </Typography>
      ) : null}
    </Box>
  )
}

// Exported so the "Comment on GitLab" section on an element's page (issue #99,
// phase C) draws a thread exactly as this panel does, controls included.
export function Thread({
  thread,
  number,
  canComment,
  busy,
  onReply,
  onResolve,
  edit,
}: {
  thread: DiscussionThread
  /** An action drawn beside Resolve (the Edit button of issue #110). */
  edit?: ReactNode
  /** 1-based, for an accessible name that tells the threads apart. */
  number: number
  canComment: boolean
  busy: boolean
  onReply: (thread: DiscussionThread, body: string) => Promise<boolean>
  onResolve: (thread: DiscussionThread) => void
}) {
  const [draft, setDraft] = useState('')
  const where = positionLabel(thread)
  const send = async () => {
    // Cleared only once GitLab took it, so a refused reply is not lost.
    if (await onReply(thread, draft)) setDraft('')
  }
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="discussion-thread">
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {where ? (
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            {where}
          </Typography>
        ) : null}
        {thread.outdated ? <Chip size="small" variant="outlined" label="Outdated" /> : null}
        {thread.resolvable ? (
          thread.resolved ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={thread.resolved_by ? `Resolved by ${thread.resolved_by}` : 'Resolved'}
            />
          ) : (
            <Chip size="small" color="warning" variant="outlined" label="Unresolved" />
          )
        ) : null}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
          {/* Fixing the element does not resolve the thread (issue #110): that
              stays the person's own click. */}
          {edit}
          {canComment && thread.resolvable ? (
            <Button
              size="small"
              disabled={busy}
              onClick={() => onResolve(thread)}
              sx={{ minWidth: 0, py: 0 }}
            >
              {thread.resolved ? 'Reopen' : 'Resolve'}
            </Button>
          ) : null}
        </Box>
      </Stack>

      <Stack spacing={1.25}>
        {thread.notes.map((note, index) => (
          <Note key={note.id ?? index} note={note} />
        ))}
      </Stack>
      {thread.truncated ? (
        <Typography variant="caption" color="text.secondary">
          More replies than are shown here - the rest are in GitLab.
        </Typography>
      ) : null}

      {canComment ? (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            fullWidth
            multiline
            maxRows={8}
            placeholder="Reply"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={busy}
            slotProps={{
              htmlInput: { maxLength: MAX_COMMENT, 'aria-label': `Reply to thread ${number}` },
            }}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={busy || !draft.trim()}
            onClick={() => void send()}
            aria-label={`Send reply to thread ${number}`}
          >
            Reply
          </Button>
        </Stack>
      ) : null}
    </Paper>
  )
}

export default function DiscussionPanel({
  slug,
  iid,
  canComment,
  reason = '',
  onChanged,
  editTargetFor,
  onEdit,
  editReason = '',
  refresh = 0,
}: {
  slug: string
  /**
   * Issue #110: the editor target of a thread's element, or null when it has
   * none. With `onEdit`, every thread anchored to an editable element gets an
   * Edit action; `editReason` (the screen's writer check) disables it.
   */
  editTargetFor?: (thread: DiscussionThread) => EditTarget | null
  onEdit?: (target: EditTarget) => void
  editReason?: string
  /** Bumped by the screen after an edit saved, to re-read the threads. */
  refresh?: number
  /** The open merge request's number; a new one is a new conversation, so it re-reads. */
  iid: number
  /** The screen's writer check. Without it the threads are read-only. */
  canComment: boolean
  /** Why the person cannot comment, shown instead of the box. */
  reason?: string
  /**
   * Called after a write that can change GitLab's "discussions must be resolved"
   * blocker - a new thread, or one resolved or reopened - so the card re-reads.
   */
  onChanged?: () => void
}) {
  const toasts = useToasts()
  const [list, setList] = useState<DiscussionList | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [fresh, setFresh] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    return api
      .listDiscussions(slug)
      .then((payload) => {
        setList(payload)
        setError(null)
      })
      .catch((failure: ApiError) => setError(failure))
      .finally(() => setLoading(false))
  }, [slug])

  useEffect(() => {
    void load()
  }, [load, iid, refresh])

  /**
   * Run one write, then re-read. A refusal is GitLab's words in a toast; a 403
   * or 409 also re-reads, because what GitLab allows or holds may have changed.
   */
  const write = async (act: () => Promise<unknown>, done: string, changesBlocker: boolean) => {
    setBusy(true)
    try {
      await act()
      toasts.succeeded(done)
      await load()
      if (changesBlocker) onChanged?.()
      return true
    } catch (failure) {
      const refused = failure as ApiError
      toasts.failed(refused.message || 'GitLab refused')
      if (refused.status === 403 || refused.status === 409) void load()
      return false
    } finally {
      setBusy(false)
    }
  }

  const startThread = async () => {
    if (await write(() => api.startDiscussion(slug, { body: fresh }), 'Comment posted', true)) {
      setFresh('')
    }
  }
  const reply = (thread: DiscussionThread, body: string) =>
    write(() => api.replyToDiscussion(slug, thread.id, { body }), 'Reply posted', false)
  const resolve = (thread: DiscussionThread) =>
    void write(
      () => api.resolveDiscussion(slug, thread.id, { resolved: !thread.resolved }),
      thread.resolved ? 'Discussion reopened' : 'Discussion resolved',
      true,
    )

  const threads = list?.threads ?? []
  return (
    <Paper sx={{ p: 2 }} data-testid="discussion-panel">
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, alignItems: 'center' }}>
        <Typography variant="h6" component="h2">
          Discussion
        </Typography>
        {list ? (
          <Chip
            size="small"
            color={list.unresolved > 0 ? 'warning' : 'default'}
            variant={list.unresolved > 0 ? 'filled' : 'outlined'}
            label={unresolvedText(list.unresolved)}
          />
        ) : null}
        {loading ? <CircularProgress size={16} aria-label="Loading the discussion" /> : null}
      </Stack>

      {error ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Could not read the discussion: {error.message}
        </Alert>
      ) : null}

      <Stack spacing={1.5}>
        {list && threads.length === 0 && !error ? (
          <Typography variant="body2" color="text.secondary">
            No comments yet.
          </Typography>
        ) : null}
        {threads.map((thread, index) => (
          <Thread
            key={thread.id}
            thread={thread}
            number={index + 1}
            canComment={canComment}
            busy={busy}
            onReply={reply}
            onResolve={resolve}
            edit={(() => {
              const target = onEdit && editTargetFor ? editTargetFor(thread) : null
              return target && onEdit ? (
                <EditElementButton target={target} reason={editReason} onEdit={onEdit} />
              ) : null
            })()}
          />
        ))}
        {list?.truncated ? (
          <Typography variant="caption" color="text.secondary">
            This merge request has more threads than are shown here - the rest are in GitLab.
          </Typography>
        ) : null}
      </Stack>

      {canComment ? (
        <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={10}
            label="New comment"
            value={fresh}
            onChange={(event) => setFresh(event.target.value)}
            disabled={busy}
            slotProps={{ htmlInput: { maxLength: MAX_COMMENT } }}
          />
          <Button
            variant="contained"
            disabled={busy || !fresh.trim()}
            onClick={() => void startThread()}
          >
            Comment
          </Button>
        </Stack>
      ) : reason ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {reason}
        </Typography>
      ) : null}
    </Paper>
  )
}
